/**
 * V70G6B — 15x15 오목판 컴포넌트.
 *   PC/Mobile 반응형. SVG 기반.
 *   착수 가능 cell 클릭 → onPlay(x,y). 미착수 영역은 커서 pointer.
 *   마지막 수 하이라이트 + 승리 라인 하이라이트.
 */
import React from 'react';
import type { OmokLastMove, OmokWinningLineCell } from './types';
import { BOARD_SIZE } from './types';

interface OmokBoardProps {
  board: number[]; // 225 cells
  lastMove: OmokLastMove | null;
  winningLine: OmokWinningLineCell[] | null;
  mySide: 'black' | 'white' | null;
  isMyTurn: boolean;
  disabled: boolean; // 플레이 중단 (status != 'playing')
  onPlay: (x: number, y: number) => void;
  size?: number;    // 전체 SVG 크기 (default 480)
}

export function OmokBoard(props: OmokBoardProps) {
  const { board, lastMove, winningLine, isMyTurn, disabled, onPlay, size = 480 } = props;
  const PAD = 20;
  const inner = size - PAD * 2;
  const step = inner / (BOARD_SIZE - 1);
  const winSet = new Set((winningLine || []).map(c => `${c.x},${c.y}`));
  const canClick = isMyTurn && !disabled;

  const handleClick = (x: number, y: number) => {
    if (!canClick) return;
    if (board[y * BOARD_SIZE + x] !== 0) return;
    onPlay(x, y);
  };

  return (
    <svg
      width="100%" height="auto"
      viewBox={`0 0 ${size} ${size}`}
      style={{ maxWidth: size, background: '#f3e3c0', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', touchAction: 'manipulation' }}
    >
      {/* 격자 */}
      {Array.from({ length: BOARD_SIZE }, (_, i) => (
        <g key={`grid-${i}`}>
          <line x1={PAD + i * step} y1={PAD} x2={PAD + i * step} y2={PAD + inner} stroke="#333" strokeWidth={1} />
          <line x1={PAD} y1={PAD + i * step} x2={PAD + inner} y2={PAD + i * step} stroke="#333" strokeWidth={1} />
        </g>
      ))}
      {/* 화점 (3,3) (3,11) (7,7) (11,3) (11,11) */}
      {[[3, 3], [3, 11], [7, 7], [11, 3], [11, 11]].map(([hx, hy]) => (
        <circle key={`star-${hx}-${hy}`} cx={PAD + hx * step} cy={PAD + hy * step} r={3} fill="#333" />
      ))}
      {/* 돌 + 클릭 영역 */}
      {Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, idx) => {
        const x = idx % BOARD_SIZE;
        const y = Math.floor(idx / BOARD_SIZE);
        const cell = board[idx];
        const cx = PAD + x * step;
        const cy = PAD + y * step;
        const isLast = lastMove && lastMove.x === x && lastMove.y === y;
        const isWin = winSet.has(`${x},${y}`);
        return (
          <g key={`cell-${idx}`}>
            {/* 클릭 영역 — 보이지 않는 사각형 (step 크기) */}
            <rect
              x={cx - step / 2} y={cy - step / 2} width={step} height={step}
              fill="transparent"
              style={{ cursor: canClick && cell === 0 ? 'pointer' : 'default' }}
              onClick={() => handleClick(x, y)}
            />
            {/* 돌 */}
            {cell !== 0 && (
              <>
                <circle
                  cx={cx} cy={cy} r={step * 0.42}
                  fill={cell === 1 ? '#1a1a1a' : '#f5f5f5'}
                  stroke={cell === 1 ? '#000' : '#888'}
                  strokeWidth={1}
                  style={{ transition: 'all 160ms ease-out' }}
                />
                {/* 광택 */}
                <circle
                  cx={cx - step * 0.1} cy={cy - step * 0.1} r={step * 0.1}
                  fill={cell === 1 ? '#555' : '#fff'}
                  opacity={0.6}
                />
              </>
            )}
            {/* 마지막 수 강조 */}
            {isLast && cell !== 0 && (
              <circle cx={cx} cy={cy} r={step * 0.15}
                fill="none" stroke="#ef4444" strokeWidth={2}
              />
            )}
            {/* 승리 라인 하이라이트 */}
            {isWin && (
              <circle cx={cx} cy={cy} r={step * 0.5}
                fill="none" stroke="#f59e0b" strokeWidth={3}
                style={{ pointerEvents: 'none' }}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

export default OmokBoard;
